import React, { useCallback, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { Alert, CircularProgress, LinearProgress } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { StyledDataGrid } from 'components/Grid/styles';
import { observer } from 'mobx-react';

import { Button } from 'components/Button';
import { SLASH_FULL_DATE_FORMAT } from 'core/constants';
import api from 'utils/api';
import { formatFloating } from 'utils/formatters/timezone';
import { useStore } from 'utils/hooks/store';
import type {
  CreateTrainingRecordPayload,
  TrainingRecord,
} from 'utils/api/modules/trainingRecords/trainingRecords.types';

import { TabComponentProps } from '../EntityDetailsContent/types';

import AddRecordModal from './AddRecordModal';
import { COMPLIANCE_STATUS_CONFIG } from './config';
import {
  ComplianceChip,
  DataGridWrapper,
  EmDash,
  EmptyState,
  TabHeader,
  TabHeaderActions,
  TabHeaderTitle,
  TabsContainer,
} from './styles';

const COMPLIANCE_STATUS_ORDER: Record<string, number> = {
  non_compliant: 0,
  outstanding: 1,
  expiring_soon: 2,
  compliant: 3,
  not_required: 4,
  archived: 5,
};

function formatDate(value: string | null | undefined): string {
  return formatFloating(value, 'date', SLASH_FULL_DATE_FORMAT) || '—';
}

function dateComparator(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
}

function statusComparator(a: string, b: string): number {
  return (COMPLIANCE_STATUS_ORDER[a] ?? 99) - (COMPLIANCE_STATUS_ORDER[b] ?? 99);
}

function hasCertificate(record: TrainingRecord): boolean {
  return record.hasCertificate;
}

const TrainingRecordsTab: React.FC<TabComponentProps> = ({ store }) => {
  const { notification } = useStore();
  const [records, setRecords] = useState<TrainingRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const contactEmail = store.details?.entity['contacts__email']?.value as string | undefined;
  const contactName = store.details?.entity['contacts__name']?.value as string | undefined;
  const [loading, setLoading] = useState(Boolean(contactEmail));

  const fetchRecords = useCallback(async () => {
    if (!contactEmail) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.trainingRecords.loadRecords(contactEmail);
      setRecords(result.records);
    } catch {
      setError('Failed to load training records. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [contactEmail]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleDownload = async (): Promise<void> => {
    if (!contactEmail) return;
    setDownloading(true);
    try {
      const result = await api.trainingRecords.bundleCertificates(contactEmail, contactName);
      window.open(result.bundleUrl, '_blank', 'noopener,noreferrer');
    } catch {
      notification.enqueueErrorSnackbar('Failed to generate certificate bundle. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleAddRecord = async (payload: CreateTrainingRecordPayload): Promise<void> => {
    await api.trainingRecords.createRecord(payload);
    void fetchRecords();
  };

  const hasCompliantCertificate = records.some(
    (r) => (r.complianceStatus === 'compliant' || r.complianceStatus === 'expiring_soon') && hasCertificate(r)
  );

  const columns: GridColDef[] = [
    {
      field: 'complianceStatus',
      headerName: 'Status',
      width: 160,
      sortComparator: statusComparator,
      renderCell: ({ row }) => {
        const config = COMPLIANCE_STATUS_CONFIG[(row as TrainingRecord).complianceStatus];
        return config ? (
          <ComplianceChip label={config.label} size="small" $background={config.background} $color={config.color} />
        ) : null;
      },
    },
    {
      field: 'moduleName',
      headerName: 'Module',
      flex: 1,
      sortable: false,
    },
    {
      field: 'certificate',
      headerName: 'Certificate',
      width: 140,
      sortable: false,
      renderCell: ({ row }) =>
        hasCertificate(row as TrainingRecord) ? (
          <Button
            variant="text"
            size="small"
            onClick={() => api.trainingRecords.openCertificate((row as TrainingRecord).id)}
          >
            View
          </Button>
        ) : (
          <EmDash>—</EmDash>
        ),
    },
    {
      field: 'courseDate',
      headerName: 'Course Date',
      width: 140,
      sortComparator: dateComparator,
      renderCell: ({ row }) => formatDate((row as TrainingRecord).courseDate),
    },
    {
      field: 'expiryDate',
      headerName: 'Expiry Date',
      width: 140,
      sortComparator: dateComparator,
      renderCell: ({ row }) => formatDate((row as TrainingRecord).expiryDate),
    },
  ];

  return (
    <TabsContainer>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button variant="text" size="small" onClick={fetchRecords}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      <TabHeader>
        <TabHeaderTitle>Training Records</TabHeaderTitle>
        <TabHeaderActions>
          <Button
            variant="outlined"
            size="medium"
            startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading || !hasCompliantCertificate}
          >
            {downloading ? 'Downloading…' : 'Download Compliant Certificates'}
          </Button>
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
            disabled={!contactEmail}
          >
            Add Record
          </Button>
        </TabHeaderActions>
      </TabHeader>
      {!contactEmail ? (
        <EmptyState>No email address found for this contact. Training records cannot be loaded.</EmptyState>
      ) : loading ? (
        <LinearProgress />
      ) : records.length === 0 ? (
        <EmptyState>No training records found.</EmptyState>
      ) : (
        <DataGridWrapper>
          <StyledDataGrid
            autoHeight
            rows={records}
            columns={columns}
            disableColumnFilter
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooter
          />
        </DataGridWrapper>
      )}
      {contactEmail && (
        <AddRecordModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSubmit={handleAddRecord}
          contactEmail={contactEmail}
        />
      )}
    </TabsContainer>
  );
};

export default observer(TrainingRecordsTab);
